// Карта с полигоном на базе MapLibre
// Компонент позволяет:
// - рисовать многоугольник кликами
// - редактировать вершины полигона (перетаскивание/удаление)
// - добавлять новые вершины через средние точки рёбер (клик/перетаскивание)
// Обмен снаружи через onChange:
// - для полигона: массив [{ lat, lon }, ...]
import { useEffect, useState, useRef, useCallback } from "react";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  Map,
  Marker,
  Source,
  Layer,
  FullscreenControl,
  NavigationControl,
} from "@vis.gl/react-maplibre";

import {
  MAP_STYLES,
  DEFAULT_VIEW_STATE,
  POLYGON_OPACITY,
  POLYGON_STROKE_WIDTH,
  FIT_BOUNDS_PADDING,
} from "@/constants/map";

import { Button } from "antd";

import TargetIcon from "@/icons/TargetIcon";

import { useObjectsLayer } from "@/hooks/useObjectsLayer";

import ObjectsLayer from "@/components/maps/ObjectsLayer";

const MARKER_SIZE = 10;
const MIDPOINT_SIZE = 9;
const DRAG_DELAY = 10;
const EPSILON = 1e-9;
const FIT_BOUNDS_DURATION = 1000;

// Утилита для расчёта bbox
const calculateBounds = (coords) => {
  let minLng = coords[0].coords[0],
    maxLng = coords[0].coords[0];
  let minLat = coords[0].coords[1],
    maxLat = coords[0].coords[1];

  coords.forEach(({ coords: [lng, lat] }) => {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};

/**
 * InputPolygonOnMap
 *
 * Пропсы:
 * - value: текущие данные [{ lat, lon }, ...]
 * - onChange: (value) => void — колбэк при изменении геометрии
 */
const InputPolygonOnMap = ({
  field,
  objectUuid,
  value,
  color = "#e66a25",
  onChange,
  highlightedIndexes = new Set(),
  hoveredIndex,
  onHoverIndex,
  style,
  ...props
}) => {
  // Ссылка на инстанс карты
  const mapRef = useRef();
  // Флаг для центрирования карты только при первом входящем значении
  const isInitialLoad = useRef(true);
  // Флаг, чтобы отличать перетаскивание от клика (во время drag не добавляем/не удаляем точки)
  const isDraggingVertex = useRef(false);

  // Текущее состояние камеры карты
  const [viewState, setViewState] = useState(DEFAULT_VIEW_STATE);

  // Массив вершин полигона: [[lng, lat], ...]
  const [polygonCoords, setPolygonCoords] = useState([]);
  // Временные координаты полигона во время перетаскивания вершины/средней точки
  const [tempPolygonCoords, setTempPolygonCoords] = useState(null);
  // Индекс перетаскиваемой вершины (если идёт drag вершины)
  const [draggedVertexIndex, setDraggedVertexIndex] = useState(null);
  // Данные перетаскиваемой средней точки: { afterIndex, coords }
  const [draggedMidpoint, setDraggedMidpoint] = useState(null);

  const { allObjects, allFields, updateBbox, setBbox } = useObjectsLayer(
    objectUuid,
    field?.field_name,
  );

  useEffect(() => {
    // Синхронизация входного value -> внутреннее состояние
    if (!value) {
      setPolygonCoords([]);
      isInitialLoad.current = true;
      return;
    }

    // Внутри работаем в проекции [lng, lat] с сохранением _index
    const coords = value.map(({ lat, lon, _index }) => ({
      coords: [lon, lat],
      _index: _index !== undefined ? _index : null,
    }));
    setPolygonCoords(coords);
  }, [value]);

  // Обработка клика по карте для рисования полигона
  const handlePolygonClick = useCallback(
    (e) => {
      // Не добавляем точку, если только что закончили перетаскивание вершины
      if (isDraggingVertex.current) {
        isDraggingVertex.current = false;
        return;
      }

      const { lng, lat } = e.lngLat;
      const newCoords = [
        ...polygonCoords,
        { coords: [lng, lat], _index: null },
      ];
      setPolygonCoords(newCoords);
      onChange(newCoords.map(({ coords: [lon, lat] }) => ({ lat, lon })));
    },
    [polygonCoords, onChange],
  );

  // Обработка перетаскивания вершины полигона
  const handleVertexDragStart = useCallback((index) => {
    isDraggingVertex.current = true;
    setDraggedVertexIndex(index);
  }, []);

  const handleVertexDrag = useCallback(
    (index, e) => {
      const { lng, lat } = e.lngLat;
      const newCoords = [...polygonCoords];
      newCoords[index] = { ...newCoords[index], coords: [lng, lat] };
      setTempPolygonCoords(newCoords); // Обновляем временные координаты в реальном времени
    },
    [polygonCoords],
  );

  const handleVertexDragEnd = useCallback(
    (index, e) => {
      const { lng, lat } = e.lngLat;
      const newCoords = [...polygonCoords];
      newCoords[index] = { ...newCoords[index], coords: [lng, lat] };
      setPolygonCoords(newCoords);
      setTempPolygonCoords(null); // Сбрасываем временные координаты
      setDraggedVertexIndex(null); // Сбрасываем индекс перетаскиваемой вершины
      onChange(newCoords.map(({ coords: [lon, lat] }) => ({ lat, lon })));

      // Сбрасываем флаг через небольшую задержку, чтобы событие onClick не сработало
      setTimeout(() => {
        isDraggingVertex.current = false;
      }, DRAG_DELAY);
    },
    [polygonCoords, onChange],
  );

  // Удаление вершины полигона по клику
  const handleVertexClick = useCallback(
    (index, e) => {
      e.originalEvent.stopPropagation();

      // Не удаляем, если это было перетаскивание
      if (isDraggingVertex.current) {
        return;
      }

      const newCoords = polygonCoords.filter((_, i) => i !== index);
      setPolygonCoords(newCoords);
      onChange(newCoords.map(({ coords: [lon, lat] }) => ({ lat, lon })));
    },
    [polygonCoords, onChange],
  );

  // Вычисляем средние точки между вершинами для добавления новых вершин
  const midpoints = useCallback(() => {
    // Стабильный расчёт средних точек, без лишних дублей на вырожденных рёбрах
    if (!polygonCoords || polygonCoords.length < 3) return [];

    const points = [];
    for (let i = 0; i < polygonCoords.length; i++) {
      const current = polygonCoords[i].coords;
      const next = polygonCoords[(i + 1) % polygonCoords.length].coords;

      const dx = next[0] - current[0];
      const dy = next[1] - current[1];
      if (Math.abs(dx) < EPSILON && Math.abs(dy) < EPSILON) {
        continue;
      }

      const midLng = (current[0] + next[0]) / 2;
      const midLat = (current[1] + next[1]) / 2;

      const mp = [midLng, midLat];
      const last = points.length ? points[points.length - 1].coords : null;
      if (
        last &&
        Math.abs(last[0] - mp[0]) < EPSILON &&
        Math.abs(last[1] - mp[1]) < EPSILON
      ) {
        continue;
      }

      points.push({
        coords: mp,
        afterIndex: i,
      });
    }

    return points;
  }, [polygonCoords]);

  // Добавление новой вершины по клику на среднюю точку
  const handleMidpointClick = useCallback(
    (afterIndex, coords, e) => {
      e.originalEvent.stopPropagation();

      const newCoords = [...polygonCoords];
      newCoords.splice(afterIndex + 1, 0, { coords, _index: null });
      setPolygonCoords(newCoords);
      onChange(newCoords.map(({ coords: [lon, lat] }) => ({ lat, lon })));
    },
    [polygonCoords, onChange],
  );

  // Добавление новой вершины при перетаскивании средней точки
  const handleMidpointDragEnd = useCallback(
    (afterIndex) => {
      if (!draggedMidpoint) return;

      const [lng, lat] = draggedMidpoint.coords;
      const newCoords = [...polygonCoords];
      newCoords.splice(afterIndex + 1, 0, { coords: [lng, lat], _index: null });
      setPolygonCoords(newCoords);
      setTempPolygonCoords(null);
      setDraggedMidpoint(null);
      onChange(newCoords.map(({ coords: [lon, lat] }) => ({ lat, lon })));

      setTimeout(() => {
        isDraggingVertex.current = false;
      }, DRAG_DELAY);
    },
    [polygonCoords, onChange, draggedMidpoint],
  );

  const handleMidpointDragStart = useCallback((afterIndex, coords) => {
    isDraggingVertex.current = true;
    setDraggedMidpoint({ afterIndex, coords });
  }, []);

  const handleMidpointDrag = useCallback((e) => {
    const { lng, lat } = e.lngLat;
    setDraggedMidpoint((prev) => {
      if (!prev) return null;
      return { ...prev, coords: [lng, lat] };
    });
  }, []);

  // GeoJSON для полигона
  const polygonGeoJSON = useCallback(() => {
    // Используем временные координаты при перетаскивании, иначе обычные
    const items = tempPolygonCoords || polygonCoords;

    if (!items || items.length < 3) return null;

    const coords = items.map((item) => item.coords);
    const coordsCopy = [...coords];
    // Замыкаем полигон
    if (
      coordsCopy[0][0] !== coordsCopy[coordsCopy.length - 1][0] ||
      coordsCopy[0][1] !== coordsCopy[coordsCopy.length - 1][1]
    ) {
      coordsCopy.push(coordsCopy[0]);
    }

    return {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [coordsCopy],
      },
    };
  }, [polygonCoords, tempPolygonCoords]);

  // Подгонка карты под полигон
  const onMapLoad = useCallback(
    (event) => {
      updateBbox(event.target);
      mapRef.current = event.target;

      if (polygonCoords.length >= 3) {
        const bounds = calculateBounds(polygonCoords);

        event.target.fitBounds(bounds, {
          padding: FIT_BOUNDS_PADDING,
          duration: 0,
        });

        setBbox({
          minLat: bounds[0][1],
          minLon: bounds[0][0],
          maxLat: bounds[1][1],
          maxLon: bounds[1][0],
        });
      }
    },
    [polygonCoords, setBbox, updateBbox],
  );

  const polygon = polygonGeoJSON();

  // Обработка клика по кнопке центрирования к полигону
  const handleTargetClick = () => {
    if (!polygonCoords || polygonCoords.length < 3 || !mapRef.current) return;

    // Вычисляем bbox по вершинам полигона
    const bounds = calculateBounds(polygonCoords);

    // Подгоняем карту под границы полигона
    mapRef.current.fitBounds(bounds, {
      padding: FIT_BOUNDS_PADDING,
      duration: FIT_BOUNDS_DURATION,
    });
  };

  // Используем временные координаты при перетаскивании, иначе обычные
  const displayCoords = tempPolygonCoords || polygonCoords;

  return (
    <Map
      {...props}
      {...viewState}
      style={{ width: "100%", height: "650px", ...style }}
      mapStyle={MAP_STYLES.voyager}
      attributionControl={false}
      cursor="crosshair"
      onMove={(evt) => {
        setViewState(evt.viewState);
      }}
      onMoveEnd={(evt) => {
        setViewState(evt.viewState);
        updateBbox(evt.target);
      }}
      onClick={handlePolygonClick}
      onLoad={onMapLoad}
    >
      <FullscreenControl />
      <NavigationControl showCompass={false} />

      {/* Отображает все поля других объектов */}
      <ObjectsLayer objects={allObjects} color="#bebebe" />
      {/* Отображает остальные поля текущего объекта */}
      <ObjectsLayer objects={allFields} color={`${color}80`} />

      {polygon && (
        <Source id="polygon-source" type="geojson" data={polygon}>
          <Layer
            id="polygon-fill"
            type="fill"
            paint={{
              "fill-color": color,
              "fill-opacity": POLYGON_OPACITY,
            }}
          />
          <Layer
            id="polygon-outline"
            type="line"
            paint={{
              "line-color": color,
              "line-width": POLYGON_STROKE_WIDTH,
            }}
          />
        </Source>
      )}

      {displayCoords.map((item, index) => {
        // Создаём стабильный ключ на основе _index и координат
        const stableKey =
          item._index !== null && item._index !== undefined
            ? `vertex-${item._index}`
            : `vertex-${item.coords[0].toFixed(6)}-${item.coords[1].toFixed(
                6,
              )}`;

        const isHighlighted =
          item._index !== null && highlightedIndexes.has(item._index);
        const isHovered = item._index !== null && item._index === hoveredIndex;

        return (
          <Marker
            key={stableKey}
            longitude={item.coords[0]}
            latitude={item.coords[1]}
            anchor="center"
            offset={[0, 0]}
            draggable
            onDragStart={() => handleVertexDragStart(index)}
            onDrag={(e) => handleVertexDrag(index, e)}
            onDragEnd={(e) => handleVertexDragEnd(index, e)}
            onClick={(e) => handleVertexClick(index, e)}
          >
            <div
              onMouseEnter={() => onHoverIndex(item._index)}
              onMouseLeave={() => onHoverIndex(null)}
              style={{
                width: isHovered ? MARKER_SIZE + 6 : MARKER_SIZE,
                height: isHovered ? MARKER_SIZE + 6 : MARKER_SIZE,
                borderRadius: "50%",

                backgroundColor: isHovered
                  ? "#52c41a"
                  : isHighlighted
                    ? "#1677ff"
                    : color,

                border: isHovered
                  ? "2px solid #f6ffed"
                  : isHighlighted
                    ? "2px solid #e6f4ff"
                    : "2px solid white",

                boxShadow: isHovered
                  ? "0 0 0 6px rgba(82,196,26,0.35)"
                  : isHighlighted
                    ? "0 0 0 4px rgba(22,119,255,0.35)"
                    : "none",

                cursor: "pointer",
                zIndex: isHovered ? 5 : 2,
              }}
              title={`Точка ${index + 1}`}
            />
          </Marker>
        );
      })}

      {polygonCoords.length >= 2 &&
        midpoints()
          .filter((midpoint) => {
            // Скрываем средние точки справа и слева от перетаскиваемой вершины
            if (draggedVertexIndex === null) return true;
            const leftIndex =
              (draggedVertexIndex - 1 + polygonCoords.length) %
              polygonCoords.length;
            return (
              midpoint.afterIndex !== draggedVertexIndex &&
              midpoint.afterIndex !== leftIndex
            );
          })
          .map((midpoint, index) => {
            const isDragging =
              draggedMidpoint &&
              draggedMidpoint.afterIndex === midpoint.afterIndex;
            const displayCoords = isDragging
              ? draggedMidpoint.coords
              : midpoint.coords;

            return (
              <Marker
                key={`mid-${index}`}
                longitude={displayCoords[0]}
                latitude={displayCoords[1]}
                anchor="center"
                offset={[0, 0]}
                draggable
                onDragStart={() =>
                  handleMidpointDragStart(midpoint.afterIndex, midpoint.coords)
                }
                onDrag={handleMidpointDrag}
                onDragEnd={() => handleMidpointDragEnd(midpoint.afterIndex)}
                onClick={(e) =>
                  handleMidpointClick(midpoint.afterIndex, midpoint.coords, e)
                }
              >
                <div
                  style={{
                    width: isDragging ? MARKER_SIZE : MIDPOINT_SIZE,
                    height: isDragging ? MARKER_SIZE : MIDPOINT_SIZE,
                    borderRadius: "50%",
                    backgroundColor: isDragging ? "#0066a6" : "#999",
                    border: "2px solid white",
                    cursor: "pointer",
                    opacity: 0.7,
                    zIndex: 1,
                  }}
                />
              </Marker>
            );
          })}

      {/* Пунктирные линии от перетаскиваемой средней точки к соседним вершинам */}
      {draggedMidpoint && (
        <Source
          id="dashed-lines-source"
          type="geojson"
          data={{
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "LineString",
                  coordinates: [
                    [
                      polygonCoords[draggedMidpoint.afterIndex].coords[0],
                      polygonCoords[draggedMidpoint.afterIndex].coords[1],
                    ],
                    draggedMidpoint.coords,
                  ],
                },
              },
              {
                type: "Feature",
                geometry: {
                  type: "LineString",
                  coordinates: [
                    draggedMidpoint.coords,
                    [
                      polygonCoords[
                        (draggedMidpoint.afterIndex + 1) % polygonCoords.length
                      ].coords[0],
                      polygonCoords[
                        (draggedMidpoint.afterIndex + 1) % polygonCoords.length
                      ].coords[1],
                    ],
                  ],
                },
              },
            ],
          }}
        >
          <Layer
            id="dashed-lines-layer"
            type="line"
            paint={{
              "line-color": "#0066a6",
              "line-width": 1.5,
              "line-dasharray": [2, 2],
            }}
          />
        </Source>
      )}

      {/* Кнопка центрирования карты к установленному пину */}
      <Button
        icon={<TargetIcon />}
        disabled={!polygon}
        style={{
          position: "absolute",
          right: 10,
          bottom: 10,
        }}
        onClick={handleTargetClick}
      >
        К полигону
      </Button>
    </Map>
  );
};

export default InputPolygonOnMap;
